import React, { useState, useEffect } from 'react';
import { Package, ClipboardList, DollarSign, Activity, Star } from 'lucide-react';
import DashboardStatCard from '../../seller-account-dashboard/components/DashboardStatCard';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

export default function ManufacturingDashboardStats() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    pendingQuotes: 0,
    activeOrders: 0,
    totalRevenue: 0,
    completedJobs: 0,
    rating: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadManufacturingStats(user.id);
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadManufacturingStats = async (userId) => {
    try {
      setLoading(true);
      setError('');

      // Get Manufacturer ID
      const { data: mfgAccount } = await supabase
        .from('manufacturer_accounts')
        .select('id, rating')
        .eq('user_id', userId)
        .single();
      
      if (!mfgAccount) {
        setLoading(false);
        return;
      }

      // In a real implementation, we would query order_items or a specific manufacturing_jobs table
      // For now, using placeholders or basic counts if tables exist
      
      setStats({
        pendingQuotes: 12, // Placeholder
        activeOrders: 5,   // Placeholder
        totalRevenue: 1250.00, // Placeholder
        completedJobs: 45, // Placeholder
        rating: mfgAccount.rating || 5.0
      });

    } catch (err) {
      console.error('Load manufacturing dashboard stats error:', err);
      setError(err?.message || 'Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        <p className="ml-3 text-muted-foreground">Loading production stats...</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
      <DashboardStatCard
        title="Pending Quotes"
        value={stats?.pendingQuotes}
        icon={<ClipboardList className="w-6 h-6" />}
        color="blue"
      />
      <DashboardStatCard
        title="Active Orders"
        value={stats?.activeOrders}
        icon={<Package className="w-6 h-6" />}
        color="orange"
      />
      <DashboardStatCard
        title="Completed Jobs"
        value={stats?.completedJobs}
        icon={<Activity className="w-6 h-6" />}
        color="green"
      />
      <DashboardStatCard
        title="MFG Revenue"
        value={`$${stats?.totalRevenue?.toFixed(2)}`}
        icon={<DollarSign className="w-6 h-6" />}
        color="purple"
      />
      <DashboardStatCard
        title="Quality Rating"
        value={stats?.rating?.toFixed(1)}
        icon={<Star className="w-6 h-6" />}
        color="yellow"
      />
    </div>
  );
}
